/**
 * Country-aware postal-code helpers for the admin address forms
 * (ticket [Todos] — validar o CEP/postal e autopreencher o endereço).
 *
 * The business/profile country drives BOTH the input mask and whether we can
 * auto-fill the address:
 *   - brazil         → CEP 00000-000  + ViaCEP address lookup (public, CORS-ok)
 *   - canada         → postal A1A 1A1 (mask only — no keyless lookup)
 *   - united-states  → ZIP 00000(-0000) (mask only)
 *   - anything else  → left as typed (we never mangle an unknown format)
 */

export interface CepResult {
    street: string;       // logradouro
    neighborhood: string; // bairro
    city: string;         // localidade
    state: string;        // UF
}

/**
 * Canonicalizes any country token to the app's stored vocabulary
 * ('brazil' | 'canada' | 'united-states'). Accepts ISO alpha-2 codes
 * ('BR'/'CA'/'US' — what the onboarding wizard + MapTiler use), the full
 * lowercase name, and common PT/EN aliases, so the two forms interoperate.
 * Empty defaults to brazil (BR-primary app); unknown tokens pass through
 * lowercased so we never mangle a format we don't understand.
 */
export function canonicalCountry(country?: string | null): string {
    const c = (country || '').trim().toLowerCase();
    if (!c) return 'brazil';
    if (c === 'br' || c === 'brazil' || c === 'brasil') return 'brazil';
    if (c === 'ca' || c === 'canada' || c === 'canadá') return 'canada';
    if (c === 'us' || c === 'usa' || c === 'united-states' || c === 'united states') return 'united-states';
    return c;
}

/** Normalizes the country value; empty defaults to brazil (BR-primary app). */
function normCountry(country?: string | null): string {
    return canonicalCountry(country);
}

/** ISO alpha-2 code for the country — what MapTiler geocoding expects to bias
 *  results. Accepts any token canonicalCountry() understands. Defaults to BR. */
export function countryToIso2(country?: string | null): string {
    switch (canonicalCountry(country)) {
        case 'canada': return 'CA';
        case 'united-states': return 'US';
        default: return 'BR';
    }
}

/** Format a postal code according to the country. Unknown countries are left
 *  untouched so we don't corrupt formats we don't understand. */
export function formatPostal(raw?: string | null, country?: string | null): string {
    const c = normCountry(country);
    const v = raw || '';
    if (c === 'canada') {
        const s = v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
        return s.length > 3 ? `${s.slice(0, 3)} ${s.slice(3)}` : s;
    }
    if (c === 'united-states') {
        const d = v.replace(/\D/g, '').slice(0, 9);
        return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    }
    if (c === 'brazil') {
        const d = v.replace(/\D/g, '').slice(0, 8);
        return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    }
    return v; // unknown country: leave as typed
}

/** Example mask shown as the input placeholder for the country. */
export function postalPlaceholder(country?: string | null): string {
    const c = normCountry(country);
    if (c === 'canada') return 'A1A 1A1';
    if (c === 'united-states') return '00000-0000';
    return '00000-000';
}

/** True when the postal code is complete enough for the country. */
export function isCompletePostal(raw?: string | null, country?: string | null): boolean {
    const c = normCountry(country);
    const v = raw || '';
    if (c === 'canada') return v.replace(/[^A-Za-z0-9]/g, '').length === 6;
    if (c === 'united-states') {
        const n = v.replace(/\D/g, '').length;
        return n === 5 || n === 9;
    }
    if (c === 'brazil') return v.replace(/\D/g, '').length === 8;
    return false;
}

/**
 * Auto-fill the address from a postal code. Only Brazil has a free keyless
 * lookup (ViaCEP); other countries return null and the user fills the rest.
 */
export async function lookupAddress(
    raw?: string | null,
    country?: string | null,
): Promise<CepResult | null> {
    if (normCountry(country) !== 'brazil') return null;
    const d = (raw || '').replace(/\D/g, '');
    if (d.length !== 8) return null;
    try {
        const res = await fetch(`https://viacep.com.br/ws/${d}/json/`);
        if (!res.ok) return null;
        const j = await res.json();
        if (j.erro) return null;
        return {
            street: j.logradouro || '',
            neighborhood: j.bairro || '',
            city: j.localidade || '',
            state: j.uf || '',
        };
    } catch {
        return null;
    }
}
